import React, { useEffect, useMemo, useState } from 'react';
import MobileSelect from '../components/MobileSelect.tsx';
import MobileDatePicker from '../components/MobileDatePicker.tsx';
import MobileFileUpload from '../components/MobileFileUpload.tsx';
import { scheduleAPI, lookupAPI, profileAPI } from '../services/api.ts';
import { toast } from 'react-toastify';
import { useConfirm } from '../context/ConfirmContext.tsx';

type LeaveType = 'Annual' | 'Sick' | 'Unpaid' | 'Emergency' | 'Other';

interface LeaveRequest {
	id: string;
	leaveType: LeaveType;
	startDate: string;
	endDate: string;
	duration: 'full' | 'half-am' | 'half-pm';
	reason: string;
	status: 'Pending' | 'Approved' | 'Rejected';
}

const Leave: React.FC = () => {
	const [showForm, setShowForm] = useState(false);
	const [leaveType, setLeaveType] = useState<LeaveType>('Annual');
	const [leaveTypeId, setLeaveTypeId] = useState<string>('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
  const [openPicker, setOpenPicker] = useState<'start' | 'end' | null>(null);
	const [reason, setReason] = useState('');
	const [requests, setRequests] = useState<LeaveRequest[]>([]);
	const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
	const [duration, setDuration] = useState<'full' | 'half-am' | 'half-pm'>('full');
	const [validationError, setValidationError] = useState<string | null>(null);
  const [historyUpcoming, setHistoryUpcoming] = useState<any[]>([]);
  const [historyPast, setHistoryPast] = useState<any[]>([]);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingLeaveId, setEditingLeaveId] = useState<number | null>(null);
  const [downloadingAttachmentKey, setDownloadingAttachmentKey] = useState<string | null>(null);
  const [deletingLeaveId, setDeletingLeaveId] = useState<number | null>(null);
  const { confirm } = useConfirm();

  // Helpers to safely format/parse dates in local timezone (avoid UTC shifting)
  const formatLocalDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const parseLocalDate = (str: string) => {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };

	// Mock balances summary card data (replace with API later)
	const [balances, setBalances] = useState<{ key: string; available: number; taken: number; note?: string; }[]>([]);

	useEffect(() => {
		// Fetch leave types from API and map to display
    scheduleAPI.getLeaveTypes()
        .then((rows: any[]) => {
            const mapped = (rows || []).map(r => ({
                key: r.LeaveType || r.leaveType || 'Leave',
                available: Number((r.Balance ?? r.balance) ?? 0),
                taken: Number((r.LeaveCount ?? r.leaveCount) ?? 0)
            }));
            setBalances(mapped);
        })
			.catch(() => {
				setBalances([]);
			});

    // Fetch master leave types for selection
    lookupAPI.getLeaveTypes()
      .then((rows) => {
        const opts = (rows || []).map((r: any) => ({ value: String(r.id), label: r.name }));
        setLeaveTypeOptions(opts);
        // default selection if not set
        if (!leaveType && opts.length > 0) setLeaveType(opts[0].label as LeaveType);
        if (!leaveTypeId && opts.length > 0) setLeaveTypeId(opts[0].value);
      })
      .catch(() => setLeaveTypeOptions([]));

		// Fetch leave history
		scheduleAPI.getLeaveHistory()
			.then((res: any) => {
				setHistoryUpcoming(res.pendingRejected || res.pendingRejected || []);
				setHistoryPast(res.approved || res.approved || []);
			})
			.catch(() => {
				setHistoryUpcoming([]);
				setHistoryPast([]);
			});
	}, []);

	const canSubmit = useMemo(() => {
		if (!leaveTypeId || !startDate) return false;
		if (duration === 'full' && !endDate) return false;
		if (duration !== 'full' && startDate !== endDate) return false;
		return true;
	}, [leaveTypeId, startDate, endDate, duration]);

	const resetForm = () => {
		setLeaveType('Annual');
		setLeaveTypeId('');
		setStartDate('');
		setEndDate('');
		setReason('');
		setSelectedFile(null);
		setDuration('full');
		setValidationError(null);
    setEditingLeaveId(null);
	};

  const loadLeaveIntoForm = (item: any) => {
    const leaveId = Number(item?.Id ?? item?.id ?? item?.LeaveId ?? item?.leaveId ?? 0);
    if (!leaveId) return;

    const normalizeDateString = (value: any) => {
      if (!value) return '';
      const date = new Date(value);
      return isNaN(date.getTime()) ? '' : formatLocalDate(date);
    };

    const normalizedStart = normalizeDateString(item.StartDate ?? item.startDate);
    const normalizedEnd = normalizeDateString(item.EndDate ?? item.endDate ?? item.StartDate ?? item.startDate);
    const halfDay = Boolean(item.HalfDay ?? item.halfDay ?? item.IsHalfDay ?? item.isHalfDay ?? false);
    const sessionId = item.LeaveSessionId ?? item.leaveSessionId ?? item.LeaveSession ?? item.leaveSession;

    setEditingLeaveId(leaveId);
    setShowForm(true);

    const leaveTypeValue = item.LeaveTypeId ?? item.leaveTypeId ?? item.LeaveTypeID ?? item.leaveTypeID;
    if (leaveTypeValue !== undefined && leaveTypeValue !== null) {
      setLeaveTypeId(String(leaveTypeValue));
      const option = leaveTypeOptions.find(o => o.value === String(leaveTypeValue));
      if (option) {
        setLeaveType(option.label as LeaveType);
      }
    } else if (item.LeaveType || item.leaveType) {
      const option = leaveTypeOptions.find(o => o.label === (item.LeaveType || item.leaveType));
      if (option) {
        setLeaveTypeId(option.value);
        setLeaveType(option.label as LeaveType);
      } else {
        setLeaveType(item.LeaveType || item.leaveType || 'Annual');
      }
    }

    setStartDate(normalizedStart);
    setEndDate(normalizedEnd);

    if (halfDay) {
      if (sessionId === 1) {
        setDuration('half-am');
      } else if (sessionId === 2) {
        setDuration('half-pm');
      } else {
        setDuration('half-am');
      }
    } else {
      setDuration('full');
    }

    setReason(item.Remarks ?? item.remarks ?? '');
    setSelectedFile(null);
    setValidationError(null);
  };

  const handleDownloadAttachment = async (leaveId: number, fileMeta: any, overrideName?: string) => {
    const rawName =
      overrideName ??
      fileMeta?.FileName ??
      fileMeta?.fileName ??
      fileMeta?.Name ??
      fileMeta?.name ??
      fileMeta?.OriginalName ??
      fileMeta?.originalName ??
      '';
    const fileName = String(rawName || '').trim();
    if (!fileName) {
      toast.error('Unable to determine file name.');
      return;
    }
    const key = `${leaveId}-${fileName}`;
    setDownloadingAttachmentKey(key);
    try {
      const blob = await profileAPI.downloadLeaveDocument(leaveId, fileName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading leave attachment:', error);
      toast.error(error?.message || 'Failed to download attachment.');
    } finally {
      setDownloadingAttachmentKey(null);
    }
  };

  const handleDeleteLeave = async (leaveId: number) => {
    if (!leaveId) return;
    const confirmDelete = await confirm('Are you sure you want to delete this leave request?');
    if (!confirmDelete) return;

    setDeletingLeaveId(leaveId);
    try {
      await profileAPI.deleteLeaveApplication(leaveId);
      toast.success('Leave request deleted successfully');

      const res: any = await scheduleAPI.getLeaveHistory();
      setHistoryUpcoming(res.pendingRejected || res.pendingRejected || []);
      setHistoryPast(res.approved || res.approved || []);
    } catch (error: any) {
      console.error('Error deleting leave request:', error);
      toast.error(error?.message || 'Failed to delete leave request');
    } finally {
      setDeletingLeaveId(null);
    }
  };

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		// Validate half-day rule: only allowed when start === end
		if (duration !== 'full' && startDate !== endDate) {
			setValidationError('Half-day is only allowed when Start Date equals End Date.');
			return;
		}
		setValidationError(null);
		if (!canSubmit) return;
		try {
			setSubmitting(true);
      const leaveTypeValue = Number(leaveTypeId || (leaveTypeOptions.find(o => o.label === leaveType)?.value || 0));
      const payload = {
        LeaveTypeId: leaveTypeValue,
        LeaveStartDate: startDate,
        LeaveEndDate: duration === 'full' ? endDate : undefined,
        HalfDay: duration !== 'full',
        LeaveSessionId: duration === 'half-am' ? 1 : duration === 'half-pm' ? 2 : undefined,
        Remarks: reason,
        FileItem: selectedFile || undefined
      };

      if (editingLeaveId) {
        await profileAPI.updateLeaveApplication({
          Id: editingLeaveId,
          ...payload
        });
      } else {
        await profileAPI.leaveApplication(payload);
      }
			// Refresh history after successful apply
			const res: any = await scheduleAPI.getLeaveHistory();
      setHistoryUpcoming(res.pendingRejected || res.pendingRejected || []);
      setHistoryPast(res.approved || res.approved || []);
			setShowForm(false);
			resetForm();
		} catch (err: any) {
			// surface backend message when available
			setValidationError(String(err?.message || err) || 'Failed to submit leave. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="px-4 py-4">
			<section className="mb-4">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">Leave</h2>
					<button
						onClick={() => {
              if (editingLeaveId) {
                resetForm();
                setShowForm(false);
              } else {
                setShowForm(v => !v);
              }
            }}
						className="bg-primary text-white px-3 py-2 rounded"
					>
						{editingLeaveId ? 'Cancel Edit' : showForm ? 'Close' : 'Apply Leave'}
					</button>
				</div>
			</section>

			{/* Balances summary cards - attractive mini tiles */}
            <div className="grid grid-cols-3 gap-3 mb-4">
                {balances.map(card => {
                    const isNegative = card.available < 0;
                    const dotClass = isNegative ? 'bg-red-500' : 'bg-emerald-500';
                    return (
                        <div key={card.key} className="bg-white rounded-xl shadow-sm p-3 border border-gray-100">
                            <div className="flex items-center justify-between mb-1">
                                <div className="text-sm font-medium truncate pr-2">{card.key}</div>
                                <span className={`w-2 h-2 rounded-full ${dotClass}`}></span>
                            </div>
                            <div className={`text-2xl font-bold leading-none ${isNegative ? 'text-red-600' : 'text-gray-900'}`}>{card.available.toFixed(1)}</div>
                            <div className="text-[10px] text-gray-500 tracking-wide">AVAILABLE</div>
                            <div className={`text-[10px] mt-1 ${isNegative ? 'text-red-600' : 'text-gray-500'}`}>{card.taken} days taken</div>
                        </div>
                    );
                })}
				{balances.length === 0 && (
					<div className="col-span-3 text-sm text-gray-500">No balances found.</div>
				)}
			</div>

			{showForm && (
				<form onSubmit={handleSubmit} className="bg-white rounded border p-3 mb-4 space-y-3">
					<div>
					<MobileSelect
						label="Leave Type"
						value={leaveTypeId || ''}
						options={leaveTypeOptions.length ? leaveTypeOptions : [
							{ value: 'Annual', label: 'Annual' },
							{ value: 'Sick', label: 'Sick' },
							{ value: 'Unpaid', label: 'Unpaid' },
							{ value: 'Emergency', label: 'Emergency' },
							{ value: 'Other', label: 'Other' },
						]}
						onChange={(val) => {
							setLeaveTypeId(val);
							const selected = leaveTypeOptions.find(o => o.value === val);
							if (selected) setLeaveType(selected.label as LeaveType);
						}}
						placeholder="Select leave type"
					/>
					<MobileFileUpload label="Attachment (optional)" file={selectedFile} onChange={setSelectedFile} />
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="block text-sm text-gray-700 mb-1">Start Date</label>
							<button onClick={() => setOpenPicker('start')} className="w-full border rounded px-2 py-2 text-left">
                {startDate || 'Select date'}
              </button>
						</div>
						<div>
							<label className="block text-sm text-gray-700 mb-1">End Date</label>
							<button onClick={() => setOpenPicker('end')} className="w-full border rounded px-2 py-2 text-left">
                {endDate || 'Select date'}
              </button>
						</div>
					</div>

					{/* Duration selector */}
					<div className="mt-3">
						<div className="block text-sm text-gray-700 mb-1">Duration</div>
						<div className="grid grid-cols-3 gap-2">
							<button type="button" onClick={() => setDuration('full')} className={`px-2 py-2 rounded border text-sm ${duration==='full' ? 'bg-primary text-white border-primary' : 'bg-white'}`}>Full Day</button>
							<button type="button" onClick={() => setDuration('half-am')} className={`px-2 py-2 rounded border text-sm ${duration==='half-am' ? 'bg-primary text-white border-primary' : 'bg-white'}`}>Half Day AM</button>
							<button type="button" onClick={() => setDuration('half-pm')} className={`px-2 py-2 rounded border text-sm ${duration==='half-pm' ? 'bg-primary text-white border-primary' : 'bg-white'}`}>Half Day PM</button>
						</div>
						{duration !== 'full' && startDate && endDate && startDate !== endDate && (
							<div className="text-xs text-red-600 mt-1">Half-day requires Start Date equals End Date.</div>
						)}
					</div>
					<div>
						<label className="block text-sm text-gray-700 mb-1">Reason (optional)</label>
						<textarea
							value={reason}
							onChange={e => setReason(e.target.value)}
							rows={3}
							className="w-full border rounded px-2 py-2"
						/>
					</div>
					{validationError && <div className="text-xs text-red-600">{validationError}</div>}
					<div className="flex justify-end gap-2">
						<button
              type="button"
              className="px-3 py-2 rounded border"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
            >
              Cancel
            </button>
						<button type="submit" disabled={!canSubmit || submitting} className={`px-3 py-2 rounded text-white ${canSubmit && !submitting ? 'bg-primary' : 'bg-gray-300 cursor-not-allowed'}`}>{submitting ? 'Submitting...' : editingLeaveId ? 'Update' : 'Submit'}</button>
					</div>
				</form>
			)}

      {/* Mobile date pickers */}
      <MobileDatePicker
        isOpen={openPicker === 'start'}
        onClose={() => setOpenPicker(null)}
        selectedDate={startDate ? parseLocalDate(startDate) : null}
        onSelectDate={(d) => { setStartDate(d ? formatLocalDate(d) : ''); setOpenPicker(null); }}
      />
      <MobileDatePicker
        isOpen={openPicker === 'end'}
        onClose={() => setOpenPicker(null)}
        selectedDate={endDate ? parseLocalDate(endDate) : null}
        onSelectDate={(d) => { setEndDate(d ? formatLocalDate(d) : ''); setOpenPicker(null); }}
      />

			{/* Tabs: Upcoming/Pending vs Past/Canceled */}
				<div className="bg-white rounded border mb-3">
				<div className="flex text-sm">
					<button onClick={() => setTab('upcoming')} className={`flex-1 py-2 ${tab === 'upcoming' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}>Pending/Rejected</button>
					<button onClick={() => setTab('past')} className={`flex-1 py-2 ${tab === 'past' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}>Approved</button>
				</div>
					<div className="divide-y">
						{(tab === 'upcoming' ? historyUpcoming : historyPast).length === 0 ? (
							<div className="p-3 text-sm text-gray-500">No requests yet.</div>
						) : (
                        (tab === 'upcoming' ? historyUpcoming : historyPast).map((item: any, idx: number) => {
                            const start = new Date(item.StartDate || item.startDate);
                            const end = new Date(item.EndDate || item.endDate);
                            const status = item.Status || item.status;
                            const wf = item.WorkFlowStatusId ?? item.workFlowStatusId;
                            const type = item.LeaveType || item.leaveType;
                            const remarks = item.Remarks || item.remarks;
                            const manager = item.ManagerRemark || item.managerRemark;
                            const leaveId = item.Id ?? item.id ?? item.LeaveId ?? item.leaveId;
                            const files =
                              Array.isArray(item.files) ? item.files :
                              Array.isArray(item.Files) ? item.Files :
                              Array.isArray(item.Attachments) ? item.Attachments :
                              [];
                            const leaveIdNumber = Number(leaveId);
                            const fmt = (d: Date) => isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
                            const statusClass = (wf === 2 || /approved/i.test(status))
                                ? 'bg-green-100 text-green-700'
                                : (wf === 3 || /reject/i.test(status))
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700';
                            const leftBorder = (wf === 2 || /approved/i.test(status)) ? 'border-green-500' : (wf === 3 || /reject/i.test(status)) ? 'border-red-500' : 'border-yellow-400';
                            const leaveCount = (item.LeaveCount ?? item.leaveCount);
                            const canEdit = (() => {
                              if (!leaveIdNumber) return false;
                              if (wf === 0 || /pending/i.test(status)) return true;
                              if ((wf === 2 || /approved/i.test(status)) && start.getTime() > Date.now()) return true;
                              return false;
                            })();
                            return (
                                <div key={idx} className={`p-3 bg-white rounded-lg shadow-sm border border-gray-100 mb-2 ${idx === 0 ? 'mt-2' : ''} border-l-4 ${leftBorder}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="font-medium pr-2 truncate">{type}</div>
                                        <div className="flex items-center gap-2">
                                            {leaveCount !== undefined && (
                                                <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">{leaveCount} day{Number(leaveCount) === 1 ? '' : 's'}</span>
                                            )}
                                            <span className={`text-xs px-2 py-1 rounded-full ${statusClass}`}>{status}</span>
                                            {canEdit && leaveId && (
                                              <div className="flex items-center gap-1">
                                                <button
                                                  onClick={() => loadLeaveIntoForm(item)}
                                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors"
                                                >
                                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232a2.5 2.5 0 113.536 3.536L7.5 20.036H4v-3.536L15.232 5.232z" />
                                                  </svg>
                                                  Edit
                                                </button>
                                                <button
                                                  onClick={() => handleDeleteLeave(leaveIdNumber)}
                                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                                  disabled={deletingLeaveId === leaveIdNumber}
                                                >
                                                  {deletingLeaveId === leaveIdNumber ? (
                                                    <>
                                                      <span className="inline-block animate-spin h-3 w-3 border-2 border-red-600 border-t-transparent rounded-full"></span>
                                                      <span>Deleting...</span>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                      </svg>
                                                      Delete
                                                    </>
                                                  )}
                                                </button>
                                              </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">{fmt(start)} — {fmt(end)}</div>
                                    {remarks && <div className="text-sm text-gray-700 mt-1">{remarks}</div>}
                                    {manager && <div className="text-xs text-gray-500 mt-1">Manager: {manager}</div>}
                                    {files.length > 0 && (
                                      <div className="mt-2 space-y-1">
                                        {files.map((fileMeta: any, fileIdx: number) => {
                                          const rawName =
                                            fileMeta?.FileName ??
                                            fileMeta?.fileName ??
                                            fileMeta?.Name ??
                                            fileMeta?.name ??
                                            fileMeta?.OriginalName ??
                                            fileMeta?.originalName ??
                                            fileMeta?.DisplayName ??
                                            fileMeta?.displayName ??
                                            `Attachment-${fileIdx + 1}`;
                                          const displayName = String(rawName || `Attachment-${fileIdx + 1}`).trim();
                                          const downloadKey = `${leaveIdNumber}-${displayName}`;
                                          const isDownloading = downloadingAttachmentKey === downloadKey;
                                          return (
                                            <button
                                              key={downloadKey}
                                              type="button"
                                              onClick={() => handleDownloadAttachment(leaveIdNumber, fileMeta, displayName)}
                                              className="inline-flex items-center gap-1 text-xs text-primary underline"
                                              disabled={isDownloading}
                                            >
                                              {isDownloading ? 'Downloading...' : displayName}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                </div>
                            );
                        })
						)}
					</div>
			</div>
		</div>
	);
};

export default Leave;


